import type { CommentPerson, CommentThreadDraft } from "@/domain/comment";
import { getCommentRepository } from "@/infrastructure/di";
import { useCreateMutation } from "../createMutation";
import { commentCache, type CommentPatch } from "./cache";
import {
  commandThread,
  type CommentReplyCommand,
  type CommentThreadCommand,
} from "./commentCommand";

const THREAD_SCOPE = "thread";

export const useCreateCommentThread = (orgId: string) =>
  useCreateMutation({
    mutationFn: (draft: CommentThreadDraft) => getCommentRepository().create(draft),
    cache: (c) => c.create(commentCache.at(orgId)),
    errorLogMessage: "Failed to create comment thread:",
  });

export const useReplyToCommentThread = (orgId: string, actor: CommentPerson | null) =>
  useCreateMutation({
    mutationFn: (command: CommentReplyCommand) =>
      getCommentRepository().reply(command.uuid, command.body),
    cache: (c) =>
      c.patch(commentCache.at(orgId), {
        id: (command) => command.uuid,
        optimistic: (edit): CommentPatch => ({ edit, actor }),
        fromResponse: (thread): CommentPatch => ({ thread }),
        scope: THREAD_SCOPE,
      }),
    errorLogMessage: "Failed to reply to comment thread:",
  });

export const useCommentThreadAction = (orgId: string, actor: CommentPerson | null) =>
  useCreateMutation({
    mutationFn: async (command: CommentThreadCommand) => {
      const repository = getCommentRepository();
      if (command.action === "editMessage")
        return repository.editMessage(command.uuid, command.body);
      if (command.action === "react" || command.action === "removeReaction")
        return repository[command.action](command.uuid, command.emoji);
      return repository[command.action](command.uuid);
    },
    cache: (c) =>
      c.patch(commentCache.at(orgId), {
        id: commandThread,
        optimistic: (edit): CommentPatch => ({ edit, actor }),
        scope: THREAD_SCOPE,
      }),
    errorLogMessage: "Comment action failed:",
  });

export const useDeleteCommentThread = (orgId: string) =>
  useCreateMutation({
    mutationFn: (uuid: string) => getCommentRepository().deleteThread(uuid),
    cache: (c) => c.remove(commentCache.at(orgId), { id: (uuid) => uuid, optimistic: true }),
    errorLogMessage: "Failed to delete comment thread:",
  });
